import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import settingApi from '@/api/setting';
import toastifyInstance from '@/utils/toastify';

const useSetting = () => {
    const queryClient = useQueryClient();

    // Query to get settings list
    const { data: settingsData, isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: settingApi.getAll,
    });
    
    const settings = settingsData?.settings || [];

    // Mutation to create new setting
    const createSetting = useMutation({
        mutationFn: ({
            key,
            value,
            description,
        }: {
            key: string;
            value: any;
            description?: string;
        }) => settingApi.set(key, value, description),
        onSuccess: () => {
            toastifyInstance.success('Setting created successfully');
            queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
        onError: () => {
            toastifyInstance.error('Failed to create setting');
        },
    });

    // Mutation to update setting
    const updateSetting = useMutation({
        mutationFn: ({
            key,
            value,
            description,
        }: {
            key: string;
            value: any;
            description?: string;
        }) => settingApi.update(key, value, description),
        onSuccess: () => {
            toastifyInstance.success('Setting updated successfully');
            queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
        onError: () => {
            toastifyInstance.error('Failed to update setting');
        },
    });

    // Mutation to delete setting
    const deleteSetting = useMutation({
        mutationFn: (key: string) => settingApi.remove(key),
        onSuccess: () => {
            toastifyInstance.success('Setting deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
        onError: () => {
            toastifyInstance.error('Failed to delete setting');
        },
    });

    // Mutation toggle active setting
    const toggleActiveSetting = useMutation({
        mutationFn: ({ key, isActive }: { key: string; isActive: boolean }) =>
            settingApi.toggleActive(key, isActive),
        onSuccess: () => {
            toastifyInstance.success('Status updated successfully');
            queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
        onError: () => {
            toastifyInstance.error('Failed to update status');
        },
    });

    return {
        settings,
        isLoading,
        createSetting,
        updateSetting,
        deleteSetting,
        toggleActiveSetting,
    };
};

export default useSetting;
